import { Box, Grow, Slide, useMediaQuery } from '@mui/material';
import React, { useEffect, useRef } from 'react';

import AIAssistant from './AIAssistant.component';
import AuthenticatedPaths from '/b2b/routing/AuthenticatedRoutes/AuthenticatedRoutes.paths';
import { PANEL_MODES } from './state/reducer';
import { makeStyles } from 'tss-react/mui';
import styles from './AIAssistantPanel.styles';
import { translate } from '/b2b/common/helpers/i18n';
import useAIAssistant from './useAIAssistant';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const useStyles = makeStyles()(styles);

/*
 * Global overlay host for the assistant's floating panel modes. Reads panelMode
 * from the provider and, in the side-panel or pop-up modes, renders the assistant
 * as a fixed overlay that leaves the page interactive behind it. Renders nothing
 * while collapsed, or when the assistant is shown fullscreen via its own route.
 * The assistant is only mounted while open so its dialogs and data effects don't
 * run in the background. This is the one panel-mode piece that knows the router,
 * so it owns the switcher's full-screen navigation and hands it down as a callback.
 */
const AIAssistantPanel = () => {
    const { classes } = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const { panelMode } = useAIAssistant();
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    const isOpen = panelMode === PANEL_MODES.SIDEBAR || panelMode === PANEL_MODES.WIDGET;

    const openMode = useRef(PANEL_MODES.SIDEBAR);
    useEffect(() => {
        if (isOpen) openMode.current = panelMode;
    }, [isOpen, panelMode]);
    const mode = isOpen ? panelMode : openMode.current;

    const instant = prefersReducedMotion || panelMode === PANEL_MODES.FULLSCREEN;

    const handleOpenFullScreen = () => history.push(AuthenticatedPaths.PRIVACY_ADVISOR);

    const panel = (
        <Box
            className={classes[mode]}
            role="complementary"
            aria-label={translate('aiAssistant.name')}
        >
            <AIAssistant
                panelMode={mode}
                onNavigate={history.push}
                onOpenFullScreen={handleOpenFullScreen}
            />
        </Box>
    );

    if (mode === PANEL_MODES.SIDEBAR) {
        return (
            <Slide
                direction="left"
                in={isOpen}
                appear
                mountOnEnter
                unmountOnExit
                timeout={instant ? 0 : undefined}
            >
                {panel}
            </Slide>
        );
    }

    return (
        <Grow
            in={isOpen}
            appear
            mountOnEnter
            unmountOnExit
            timeout={instant ? 0 : theme.transitions.duration.shortest}
            style={{ transformOrigin: 'bottom right' }}
        >
            {panel}
        </Grow>
    );
};

export default AIAssistantPanel;
